import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  name?: string; // Name of the boundary for logging
  showDetails?: boolean;
  compact?: boolean; // For inline error displays
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ''}] Caught an error:`, error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Call optional error handler
    this.props.onError?.(error, errorInfo);
    
    // Log to localStorage for debugging
    try {
      const errorLog = JSON.parse(localStorage.getItem('luca_error_log') || '[]');
      errorLog.push({
        timestamp: Date.now(),
        boundary: this.props.name || 'unnamed',
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
      // Keep only last 10 errors
      localStorage.setItem('luca_error_log', JSON.stringify(errorLog.slice(-10)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Compact error display for inline components
      if (this.props.compact) {
        return (
          <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span className="text-sm">Something went wrong.</span>
            <button
              onClick={this.handleReset}
              className="ml-auto text-xs px-2 py-1 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors"
            >
              Retry
            </button>
          </div>
        );
      }

      // Full error display
      return (
        <div className="min-h-[300px] bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full border border-slate-700">
            <div className="text-center">
              {/* Error Icon */}
              <div className="inline-flex p-4 bg-red-500/10 rounded-full mb-4">
                <AlertTriangle size={32} className="text-red-500" />
              </div>
              
              {/* Title */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Oops! Something went wrong
              </h1>
              
              {/* Description */}
              <p className="text-sm sm:text-base text-slate-400 mb-6">
                {this.props.name 
                  ? `An error occurred in the ${this.props.name} component.`
                  : 'The app encountered an unexpected error.'
                }
              </p>
              
              {/* Error Details (toggleable) */}
              {(this.props.showDetails !== false) && this.state.error && (
                <details className="text-left mb-6 bg-slate-900 rounded-lg overflow-hidden">
                  <summary className="px-4 py-2 text-slate-400 cursor-pointer hover:text-slate-300 hover:bg-slate-800 transition-colors flex items-center gap-2 text-sm">
                    <Bug size={14} />
                    Technical Details
                  </summary>
                  <div className="p-3 border-t border-slate-700">
                    <p className="text-xs text-red-400 font-mono mb-2 break-all">
                      {this.state.error.message}
                    </p>
                    {this.state.error.stack && (
                      <pre className="text-[10px] text-slate-500 bg-slate-950 p-2 rounded overflow-auto max-h-32 scrollbar-thin">
                        {this.state.error.stack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors text-sm"
                >
                  <RefreshCw size={16} />
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg font-medium transition-colors text-sm"
                >
                  <RefreshCw size={16} />
                  Reload Page
                </button>
              </div>
              
              {/* Home Link */}
              <button
                onClick={this.handleGoHome}
                className="mt-4 text-xs text-slate-500 hover:text-slate-400 transition-colors flex items-center justify-center gap-1"
              >
                <Home size={12} />
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easy wrapping
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  boundaryProps?: Omit<Props, 'children'>
) => {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...boundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;
